import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { Eye, EyeOff, AlertCircle, ArrowLeft } from "lucide-react";
import { Input } from "../../share/ui/input";
import loginBg from "../../assets/img/login.png";
import maskgroup from "../../assets/img/maskgroup.png";
import logo from "../../assets/logo/evocare.svg";
import imageSuccess from "../../assets/img/resetPassword.png";
import { cn } from "../../lib/utils";
import { resetPassword } from "../../api/login/authService";
import { useTranslation } from "react-i18next";

export default function ResetPasswordPage() {
  const { t } = useTranslation();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<{
    newPassword?: string;
    confirmPassword?: string;
  }>({});
  const navigate = useNavigate();

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: { newPassword?: string; confirmPassword?: string } = {};

    if (!newPassword) newErrors.newPassword = t("login.errorNewPassword");
    if (!confirmPassword)
      newErrors.confirmPassword = t("login.errorConfirmPassword");
    if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = t("login.errorNotMatch");
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    try {
      setIsLoading(true);
      await resetPassword(newPassword);
      toast.success(t("login.resetSuccessToast"));
      setTimeout(() => {
        setIsSuccess(true);
      }, 1500);
    } catch (err) {
      toast.error(t("login.resetFailed"));
      console.error("Reset failed:", err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen">
      {/* Left side - Login Form */}
      <div className="w-full md:w-3/5 flex items-start pt-16 justify-center bg-white md:px-8 px-4 relative">
        <img
          src={maskgroup}
          alt="Mask Group"
          width={450}
          className="absolute top-0 left-10 pointer-events-none z-10"
          style={{ height: "auto" }}
        />
        <div className="max-w-md w-full relative z-20">
          <img
            src={logo}
            alt="EvoCare Logo"
            width={230}
            height={60}
            className="mb-6 pl-1"
          />

          {isSuccess ? (
            <div className="text-start p-1">
              <h2 className="text-xl font-bold text-gray-800 mb-2">
                {t("login.successTitle")}
              </h2>
              <p className="text-gray-600 mb-6">{t("login.successDesc")}</p>
              <img
                src={imageSuccess}
                alt="Success"
                width={160}
                height={160}
                className="mx-auto mb-6 text-start"
              />
              <button
                type="button"
                onClick={() => navigate("/login")}
                className="w-full py-3 bg-orange-50 text-orange-700 hover:bg-orange-100 rounded-md font-medium shadow flex justify-center items-center gap-2 transition"
              >
                <ArrowLeft size={18} />
                <span>{t("login.backToLogin")}</span>
              </button>
            </div>
          ) : (
            <>
              <h2 className="text-xl font-semibold text-gray-800 mb-3">
                {t("login.resetTitle")}
              </h2>
              <p className="text-sm text-gray-500 mb-6">
                {t("login.resetDesc")}
              </p>

              <form onSubmit={handleResetPassword} className="space-y-8">
                {/* New Password */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 ">
                    {t("login.newPassword")}
                  </label>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={newPassword}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setNewPassword(e.target.value)
                      }
                      placeholder={t("login.enterPassword")}
                      className={cn(
                        "pr-10",
                        errors.newPassword && "border-red-500"
                      )}
                    />
                    <div className="absolute inset-y-0 right-3 flex items-center text-gray-500 cursor-pointer">
                      {showPassword ? (
                        <EyeOff
                          size={18}
                          onClick={() => setShowPassword(false)}
                        />
                      ) : (
                        <Eye size={18} onClick={() => setShowPassword(true)} />
                      )}
                      {errors.newPassword && (
                        <AlertCircle
                          className="absolute right-6 top-3 text-red-500"
                          size={18}
                        />
                      )}
                    </div>
                  </div>
                  {errors.newPassword && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.newPassword}
                    </p>
                  )}
                </div>

                {/* Confirm new Password */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 ">
                    {t("login.confirmNewPassword")}
                  </label>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setConfirmPassword(e.target.value)
                      }
                      placeholder={t("login.enterNewPassword")}
                      className={cn(
                        "pr-10",
                        errors.confirmPassword && "border-red-500"
                      )}
                    />
                    <div className="absolute inset-y-0 right-3 flex items-center text-gray-500 cursor-pointer">
                      {showPassword ? (
                        <EyeOff
                          size={18}
                          onClick={() => setShowPassword(false)}
                        />
                      ) : (
                        <Eye size={18} onClick={() => setShowPassword(true)} />
                      )}
                      {errors.confirmPassword && (
                        <AlertCircle
                          className="absolute right-6 top-3 text-red-500"
                          size={18}
                        />
                      )}
                    </div>
                  </div>
                  {errors.confirmPassword && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.confirmPassword}
                    </p>
                  )}
                </div>

                {/* Submit button */}
                <button
                  type="submit"
                  className="w-full py-3 bg-orange-600 shadow-lg hover:bg-orange-700 text-white rounded-md font-medium transition"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <span className="flex items-center justify-center">
                      <svg
                        className="animate-spin h-5 w-5 mr-2 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      {t("login.loading")}
                    </span>
                  ) : (
                    t("login.resetButton")
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className="w-full py-3 bg-orange-50 text-orange-700 hover:bg-orange-100 rounded-md font-medium shadow flex justify-center items-center gap-2 transition"
                >
                  <ArrowLeft size={18} />
                  <span>{t("login.back")}</span>
                </button>
              </form>
            </>
          )}
        </div>
      </div>

      {/* Right side - Background Image */}
      <div className="hidden md:block md:w-4/5 relative">
        <img
          src={loginBg}
          alt="Login Background"
          className="object-cover brightness-95 w-full h-full"
        />
      </div>
    </div>
  );
}
